import { useSnackbar } from 'notistack';
import Iconify from '@emfood/shared/src/components/iconify';

import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import { Box, Card, Grid, Tooltip, Skeleton, IconButton, Typography } from '@mui/material';

import { useGetProductImages } from 'src/api/hooks/api-hooks';
import { GenerationHistoryItemPreviewProps } from 'src/api/models/generation-history';

interface DialogProps {
  open: boolean;
  onClose: () => void;
  storyData: GenerationHistoryItemPreviewProps | null;
}

export default function PreviewStoryDialog({ open, onClose, storyData }: DialogProps) {
  const { enqueueSnackbar } = useSnackbar();

  // Fetch product images - only when dialog is open and we have story data
  const { data: productImages, isLoading: isLoadingImages } = useGetProductImages(
    open && storyData?.productId ? storyData.productId : null
  );

  const handleCopyContent = () => {
    if (storyData?.content) {
      navigator.clipboard.writeText(storyData.content);
      enqueueSnackbar('Story content copied to clipboard!', { variant: 'success' });
    }
  };

  const handleDownloadImage = async (imageUrl: string, filename: string) => {
    try {
      const response = await fetch(imageUrl);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      enqueueSnackbar('Image downloaded successfully!', { variant: 'success' });
    } catch (error) {
      enqueueSnackbar('Failed to download image', { variant: 'error' });
    }
  };

  const parseImages = (imageString: string) => {
    if (!imageString) return [];
    return imageString
      .split(',')
      .map((url) => url.trim())
      .filter(Boolean);
  };

  const productHeroImageUrl = productImages?.productHeroImage;
  const productImageUrls = parseImages(productImages?.productImages || '');
  const producerImageUrls = parseImages(productImages?.producerImages || '');
  const producerLogoUrl = productImages?.producerLogo;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">Story Preview</Typography>
          {storyData && (
            <Tooltip title="Copy entire story">
              <IconButton onClick={handleCopyContent} size="small">
                <Iconify icon="mingcute:copy-line" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </DialogTitle>

      <DialogContent>
        {!storyData ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
            <Typography variant="body2" color="text.secondary">
              No story data available
            </Typography>
          </Box>
        ) : (
          <>
            <Typography variant="h6" gutterBottom>
              Generated Story Content
            </Typography>

            <Card variant="outlined" sx={{ p: 2, backgroundColor: 'grey.50' }}>
              <Typography
                variant="body1"
                sx={{
                  whiteSpace: 'pre-wrap',
                  lineHeight: 1.6,
                  fontFamily: 'inherit',
                }}
              >
                {storyData.content}
              </Typography>
            </Card>

            {/* Product Images Section */}
            <Box mt={4}>
              <Typography variant="h6" gutterBottom>
                Images
              </Typography>

              {isLoadingImages ? (
                <Grid container spacing={2}>
                  {[1, 2, 3].map((item) => (
                    <Grid item xs={6} sm={4} md={3} key={item}>
                      <Skeleton variant="rectangular" height={120} />
                    </Grid>
                  ))}
                </Grid>
              ) : (
                <Grid container spacing={2} sx={{ mt: 1 }}>
                  {/* Show placeholder images when no actual images are available */}
                  {!productHeroImageUrl &&
                  productImageUrls.length === 0 &&
                  producerImageUrls.length === 0 &&
                  !producerLogoUrl ? (
                    <>
                      {[1, 2, 3].map((item) => (
                        <Grid item xs={12} sm={6} md={4} key={`placeholder-${item}`}>
                          <Card
                            sx={{
                              height: 180,
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'center',
                              justifyContent: 'center',
                              backgroundColor: 'grey.100',
                              border: '2px dashed',
                              borderColor: 'grey.300',
                            }}
                          >
                            <Iconify
                              icon="mingcute:image-line"
                              width={48}
                              height={48}
                              sx={{ color: 'grey.400', mb: 1 }}
                            />
                            <Typography variant="body2" color="text.secondary" textAlign="center">
                              No Image Available
                            </Typography>
                          </Card>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ mt: 1, display: 'block', textAlign: 'center' }}
                          >
                            Product Image {item}
                          </Typography>
                        </Grid>
                      ))}
                    </>
                  ) : (
                    <>
                      {/* Product Hero Image */}
                      {productHeroImageUrl && (
                        <Grid item xs={12} sm={6} md={4} key="product-hero">
                          <Card
                            sx={{
                              position: 'relative',
                              transition: 'all 0.2s ease-in-out',
                              height: 180,
                              overflow: 'hidden',
                            }}
                          >
                            <Box
                              component="img"
                              src={productHeroImageUrl}
                              alt="Product Hero"
                              sx={{
                                width: '100%',
                                height: 140,
                                objectFit: 'cover',
                              }}
                              onError={(e) => {
                                const target = e.target as HTMLImageElement;
                                const parent = target.parentElement;
                                if (parent) {
                                  parent.innerHTML = `
                                  <div style="display: flex; flex-direction: column; align-items: center; justify-content: center; height: 140px; background-color: #f5f5f5; border: 2px dashed #ccc;">
                                    <div style="font-size: 12px; color: #666;">Hero image not available</div>
                                  </div>
                                `;
                                }
                              }}
                            />
                            <Box
                              sx={{
                                position: 'absolute',
                                top: 8,
                                right: 8,
                                backgroundColor: 'rgba(0,0,0,0.7)',
                                borderRadius: 1,
                                p: 0.5,
                                opacity: 0.8,
                                cursor: 'pointer',
                                '&:hover': { opacity: 1 },
                              }}
                              onClick={() =>
                                handleDownloadImage(productHeroImageUrl, 'product-hero-image.jpg')
                              }
                            >
                              <Iconify icon="mingcute:download-line" color="white" width={16} />
                            </Box>
                            <Box sx={{ p: 1, backgroundColor: 'background.paper' }}>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ fontWeight: 500 }}
                              >
                                Product Main Image
                              </Typography>
                            </Box>
                          </Card>
                        </Grid>
                      )}

                      {/* Product Images */}
                      {productImageUrls.map((imageUrl, index) => (
                        <Grid item xs={12} sm={6} md={4} key={`product-${index}`}>
                          <Card
                            sx={{
                              position: 'relative',
                              transition: 'all 0.2s ease-in-out',
                              height: 180,
                              overflow: 'hidden',
                            }}
                          >
                            <Box
                              component="img"
                              src={imageUrl}
                              alt={`Product ${index + 1}`}
                              sx={{
                                width: '100%',
                                height: 140,
                                objectFit: 'cover',
                              }}
                              onError={(e) => {
                                const target = e.target as HTMLImageElement;
                                const parent = target.parentElement;
                                if (parent) {
                                  parent.innerHTML = `
                                  <div style="display: flex; flex-direction: column; align-items: center; justify-content: center; height: 140px; background-color: #f5f5f5; border: 2px dashed #ccc;">
                                    <div style="font-size: 12px; color: #666;">Image not available</div>
                                  </div>
                                `;
                                }
                              }}
                            />
                            <Box
                              sx={{
                                position: 'absolute',
                                top: 8,
                                right: 8,
                                backgroundColor: 'rgba(0,0,0,0.7)',
                                borderRadius: 1,
                                p: 0.5,
                                opacity: 0.8,
                                cursor: 'pointer',
                                '&:hover': { opacity: 1 },
                              }}
                              onClick={() =>
                                handleDownloadImage(imageUrl, `product-image-${index + 1}.jpg`)
                              }
                            >
                              <Iconify icon="mingcute:download-line" color="white" width={16} />
                            </Box>
                            <Box sx={{ p: 1, backgroundColor: 'background.paper' }}>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ fontWeight: 500 }}
                              >
                                Product Image {index + 1}
                              </Typography>
                            </Box>
                          </Card>
                        </Grid>
                      ))}

                      {producerLogoUrl && (
                        <Grid item xs={12} sm={6} md={4} key="producer-logo">
                          <Card
                            sx={{
                              position: 'relative',
                              transition: 'all 0.2s ease-in-out',
                              height: 180,
                              overflow: 'hidden',
                            }}
                          >
                            <Box
                              component="img"
                              src={producerLogoUrl}
                              alt="Producer Logo"
                              sx={{
                                width: '100%',
                                height: 140,
                                objectFit: 'contain',
                                backgroundColor: 'white',
                                p: 1,
                              }}
                              onError={(e) => {
                                const target = e.target as HTMLImageElement;
                                const parent = target.parentElement;
                                if (parent) {
                                  parent.innerHTML = `
                                  <div style="display: flex; flex-direction: column; align-items: center; justify-content: center; height: 140px; background-color: #f5f5f5; border: 2px dashed #ccc;">
                                    <div style="font-size: 12px; color: #666;">Producer Logo not available</div>
                                  </div>
                                `;
                                }
                              }}
                            />
                            <Box
                              sx={{
                                position: 'absolute',
                                top: 8,
                                right: 8,
                                backgroundColor: 'rgba(0,0,0,0.7)',
                                borderRadius: 1,
                                p: 0.5,
                                opacity: 0.8,
                                cursor: 'pointer',
                                '&:hover': { opacity: 1 },
                              }}
                              onClick={() =>
                                handleDownloadImage(producerLogoUrl, 'producer-logo.jpg')
                              }
                            >
                              <Iconify icon="mingcute:download-line" color="white" width={16} />
                            </Box>
                            <Box sx={{ p: 1, backgroundColor: 'background.paper' }}>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ fontWeight: 500 }}
                              >
                                Producer Logo
                              </Typography>
                            </Box>
                          </Card>
                        </Grid>
                      )}

                      {producerImageUrls.map((imageUrl, index) => (
                        <Grid item xs={12} sm={6} md={4} key={`producer-${index}`}>
                          <Card
                            sx={{
                              position: 'relative',
                              transition: 'all 0.2s ease-in-out',
                              height: 180,
                              overflow: 'hidden',
                            }}
                          >
                            <Box
                              component="img"
                              src={imageUrl}
                              alt={`Producer ${index + 1}`}
                              sx={{
                                width: '100%',
                                height: 140,
                                objectFit: 'cover',
                              }}
                              onError={(e) => {
                                const target = e.target as HTMLImageElement;
                                const parent = target.parentElement;
                                if (parent) {
                                  parent.innerHTML = `
                                  <div style="display: flex; flex-direction: column; align-items: center; justify-content: center; height: 140px; background-color: #f5f5f5; border: 2px dashed #ccc;">
                                    <div style="font-size: 12px; color: #666;">Producer image not available</div>
                                  </div>
                                `;
                                }
                              }}
                            />
                            <Box
                              sx={{
                                position: 'absolute',
                                top: 8,
                                right: 8,
                                backgroundColor: 'rgba(0,0,0,0.7)',
                                borderRadius: 1,
                                p: 0.5,
                                opacity: 0.8,
                                cursor: 'pointer',
                                '&:hover': { opacity: 1 },
                              }}
                              onClick={() =>
                                handleDownloadImage(imageUrl, `producer-image-${index + 1}.jpg`)
                              }
                            >
                              <Iconify icon="mingcute:download-line" color="white" width={16} />
                            </Box>
                            <Box sx={{ p: 1, backgroundColor: 'background.paper' }}>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ fontWeight: 500 }}
                              >
                                Producer Image {index + 1}
                              </Typography>
                            </Box>
                          </Card>
                        </Grid>
                      ))}
                    </>
                  )}
                </Grid>
              )}
            </Box>
          </>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} variant="outlined" color="inherit">
          Close
        </Button>
        {storyData && (
          <Button
            onClick={handleCopyContent}
            variant="contained"
            startIcon={<Iconify icon="mingcute:copy-line" />}
          >
            Copy Story
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
